import classNames from 'classnames';
import React, { ReactNode, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { User } from '../../../../entities/User';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { Header } from './Header';
import OfferRequisitionInfoCard from './OfferRequisitionCard/OfferRequisitionInfoCard';
import OfferDetailsCard from './OfferRequisitionCard/OfferDetailsCard';
import ApprovalFlowCard from '../../../../components/ApplicantTracking/Requisition/ApprovalFlowCard';
import DocumentsAndSignatoryInfoCard from './OfferRequisitionCard/DocumentsAndSignatoryInfoCard';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../../../config/queryClient';
import { useOfferRequisition } from '../../../../queries/v1/applicant_tracking/useOfferRequisition';
import { SendSignableOfferModal } from '../../SignableOffers/SendSignableOfferModal';
import { DefaultAlert } from '../../../../components/DefaultAlert';
import { AlertObject } from '../../../../components/Alert';
import {
  getQueryParam,
  removeQueryParamFromCurrentTab,
} from '../../../../utils/url';

interface PropTypes {
  currentUser: User;
  offerRequisitionId: number;
}

interface CardPropTypes {
  className: string;
  children: ReactNode;
}

const expandParams = [
  'owner_user',
  'approvers',
  'approvers.user',
  'signable_offer',
  'signable_offer.email',
  'signable_offer.email.attachments',
  'signable_offer.application',
  'signable_offer.application.candidate',
  'signable_offer.application.job',
  'signable_offer.offer_template',
  'signable_offer.offer_template.signable_documents',
  'signable_offer.offer_template.signatories',
  'signable_offer.offer_template.signatories.user',
  'signable_offer.signing_links',
  'signable_offer.signing_links.user',
];

const EDIT_MODE_QUERY_PARAM = 'edit';

function Card(props: CardPropTypes) {
  return (
    <Col sm='12' md='4' className={classNames('px-0 gy-4', props.className)}>
      {props.children}
    </Col>
  );
}

function Body(props: PropTypes) {
  const [isLoading, setIsLoading] = useState(false);
  const [signableOfferModalOpen, setSignableOfferModalOpen] = useState(
    getQueryParam(EDIT_MODE_QUERY_PARAM) === 'true',
  );
  const [alert, setAlert] = useState<AlertObject>(null);

  const {
    data: offerRequisition,
    refetch: reloadOfferRequisition,
    isLoading: isOfferRequisitionLoading,
  } = useOfferRequisition(props.offerRequisitionId, expandParams);

  if (isLoading || isOfferRequisitionLoading) return <LoadingSpinner />;

  const handleSendOfferClose = () => {
    setSignableOfferModalOpen(false);
    reloadOfferRequisition();
  };

  removeQueryParamFromCurrentTab(EDIT_MODE_QUERY_PARAM);

  return (
    <>
      <DefaultAlert alert={alert} setAlert={setAlert} />
      <Header
        offerRequisition={offerRequisition}
        currentUser={props.currentUser}
        setLoadingState={setIsLoading}
        onEditOfferRequisition={() => setSignableOfferModalOpen(true)}
        reloadOfferRequisition={reloadOfferRequisition}
      />
      <Row className='gx-4 my-4 mx-0'>
        <Card className='pe-2h'>
          <OfferRequisitionInfoCard offerRequisition={offerRequisition} />
          <DocumentsAndSignatoryInfoCard offerRequisition={offerRequisition} />
        </Card>
        <Card className='px-2h'>
          <OfferDetailsCard offerRequisition={offerRequisition} />
        </Card>
        <Card className='ps-2h'>
          <ApprovalFlowCard
            approvers={offerRequisition?.approvers}
            currentUser={props.currentUser}
          />
        </Card>
      </Row>
      <SendSignableOfferModal
        editMode
        offerRequisitionFlowEnabled
        applicationId={offerRequisition.signableOffer.application.id}
        hasEmailConnected={props.currentUser.hasEmailConnected}
        isOpen={signableOfferModalOpen}
        to={offerRequisition.signableOffer.application.candidate.email}
        signableOffer={offerRequisition.signableOffer}
        onClose={handleSendOfferClose}
        setAlert={setAlert}
      />
    </>
  );
}

export default function ShowOfferRequisitionPage(props: PropTypes) {
  return (
    <QueryClientProvider client={queryClient}>
      <Body {...props} />
    </QueryClientProvider>
  );
}
